import { useEffect, useState } from 'react';

import {
  useBillingRepositoryInjector,
  usePaymentMethodRepositoryInjector,
  usePointRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import { useAccountContext } from 'application/providers/AccountProvider';
import AnalyticsService, {
  CalledFromValues,
  EventNameAMP,
  EventNameGTM,
  ParamNameAMP,
} from 'application/services/AnalyticsService';
import {
  PointParamNameAMP,
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService/ampTypes';
import { SelectPlayerFlowType } from 'application/components/common/SelectPlayerFlow/types';
import { PointWithDevice } from 'application/presenters/point/types';
import { PointsListCheckboxGroupPointType } from 'application/components/PointItemContent/types';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { matchPath } from 'application/providers/RouterProvider';

import { DeviceTypes } from 'domain/api/graphql/generated';

import { StreamBindSuccessModalType, UseStreamBindSuccessModalViewModel } from './types';
import { BREAK_EDGE_VALUE } from './consts';

interface Props {
  flowType?: SelectPlayerFlowType;
  selectedPointsProps: PointsListCheckboxGroupPointType[];
  pointId?: string;
  pathname: string;
  redirectToPointsPage: () => void;
  redirectToStreamsPage: () => void;
  close: (count?: number) => void;
  successCallback?: () => void;
  replaceModal: <T extends ModalTypes>(
    type: T,
    modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
    onModalCloseCallback?: () => void
  ) => void;
}

const useStreamBindSuccessModalViewModel = ({
  flowType,
  selectedPointsProps,
  pointId,
  pathname,
  redirectToPointsPage,
  redirectToStreamsPage,
  close,
  successCallback,
  replaceModal,
}: Props): UseStreamBindSuccessModalViewModel => {
  const { watchBillingInfo } = useBillingRepositoryInjector();
  const { readPaymentMethod } = usePaymentMethodRepositoryInjector();
  const { clearCurrentPoint, readCurrentPoint } = usePointRepositoryInjector();

  const { currentCompany } = useAccountContext();

  const { paymentMethod } = readPaymentMethod(currentCompany!.id);
  const { hasActiveLicenses, hasInactiveLicenses } = watchBillingInfo(currentCompany!.id);

  const currentPoint = readCurrentPoint();

  const [isAllPointsVisible, setIsAllPointsVisible] = useState<boolean>(false);

  const isNeedPayment = (hasActiveLicenses && !paymentMethod) || hasInactiveLicenses;

  const selectedPoints = selectedPointsProps.length !== 0 ? selectedPointsProps : [currentPoint as PointWithDevice];

  const showWebPlayerButtonForStreamPage =
    selectedPoints.length === 1 &&
    flowType === SelectPlayerFlowType.BIND_STREAM &&
    selectedPoints[0]?.device?.type === DeviceTypes.Webplayer;

  const showWebPlayerButtonForPointPage =
    selectedPoints.length === 1 &&
    flowType !== SelectPlayerFlowType.BIND_STREAM &&
    currentPoint?.device?.type === DeviceTypes.Webplayer &&
    currentPoint?.isEnabled;

  const showWebPlayerButton = showWebPlayerButtonForStreamPage || showWebPlayerButtonForPointPage;

  const isRunStreamOnExistingPointsFlow =
    selectedPoints.length > 1 || (!!selectedPoints[0]?.stream?.id && !!selectedPoints[0]?.device?.id);

  const isSettingsFinished =
    (!!currentPoint?.device?.type && !!currentPoint.stream?.id) || isRunStreamOnExistingPointsFlow;

  const isMatchPointsPathname = Boolean(matchPath({ path: '/points/*' }, pathname));
  const isPointPage =
    (flowType === SelectPlayerFlowType.CHANGE_DEVICE || flowType === SelectPlayerFlowType.CREATE_POINT) &&
    isMatchPointsPathname;
  const pointHasStream = !!(currentPoint && currentPoint?.stream?.id);

  const isChangeStreamFlow = flowType === SelectPlayerFlowType.CHANGE_DEVICE && pointHasStream;

  const showStreamsButton = !pointHasStream && !isSettingsFinished;
  const showExpandButton = selectedPoints.length > BREAK_EDGE_VALUE && !isAllPointsVisible;

  const onBindPointSuccessClick = () => {
    if (!showWebPlayerButton) {
      redirectToPointsPage();
      close();
    }

    if (showWebPlayerButton) {
      AnalyticsService.event(EventNameAMP.OPEN_WEBPLAYER_PRESSED, {
        [PointParamNameAMP.POINT_ID]: pointId,
        [ParamNameAMP.CALLED_FROM]: CalledFromValues.STREAM_BC_SUCCESS,
      });

      const isPointWithStreamDisabled =
        currentCompany?.isBillingEnabled &&
        flowType === SelectPlayerFlowType.CHANGE_DEVICE &&
        !currentPoint?.isEnabled &&
        currentPoint?.stream;

      if (isPointWithStreamDisabled || (flowType !== SelectPlayerFlowType.CHANGE_DEVICE && isNeedPayment)) {
        replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
      } else {
        setTimeout(() => {
          window.open(selectedPoints[0]?.device?.requiredVersionUrl!);
        }, 100);

        close();
      }
    }

    successCallback?.();
  };

  const onClose = () => {
    clearCurrentPoint();
    successCallback?.();
    close();
  };

  const onChangeStreamClick = () => {
    AnalyticsService.event(EventNameAMP.ADDRESS_STREAM_ADD_CLICKED, {
      [ParamNameAMP.TYPE]: 'success bind device modal',
    });

    redirectToStreamsPage();
    close();
  };

  useEffect(() => {
    let successModalType = StreamBindSuccessModalType.COMPLETED;

    if (showWebPlayerButton) {
      successModalType = StreamBindSuccessModalType.OPEN_WEBPLAYER;
    }

    if (showStreamsButton) {
      successModalType = StreamBindSuccessModalType.SELECT_WAVE;
    }

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_BIND_SUCCESS_MODAL_SHOWED, {
      [StreamBindFlowParamNameAMP.SUCCESS_MODAL_TYPE]: successModalType,
    });

    if (successModalType !== StreamBindSuccessModalType.SELECT_WAVE) {
      AnalyticsService.eventGTM(EventNameGTM.WAVE_INITIATION_VIEWED);
    }

    return () => {
      if (flowType === SelectPlayerFlowType.BIND_STREAM) {
        clearCurrentPoint();
      }
    };
  }, []);

  return {
    selectedPoints,
    currentPoint,
    isNeedPayment,
    isPointPage,
    showStreamsButton,
    showWebPlayerButton,
    showExpandButton,
    isChangeStreamFlow,
    isSettingsFinished,
    isBillingEnabled: Boolean(currentCompany?.isBillingEnabled),
    onBindPointSuccessClick,
    onChangeStreamClick,
    onClose,
    setIsAllPointsVisible,
  };
};

export default useStreamBindSuccessModalViewModel;
